import type { ReactNode } from 'react';
import { createElement } from 'react';

/**
 * Open an address in Google Maps in a new tab
 */
export function openInGoogleMaps(address: string): void {
  if (!address || address.trim() === '') return;

  const encodedAddress = encodeURIComponent(address);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  window.open(googleMapsUrl, '_blank');
}

/**
 * Get user initials from name parts or email
 */
export function getUserInitials(
  firstName?: string | null,
  lastName?: string | null,
  name?: string | null,
  email?: string | null
): string {
  if (firstName || lastName) {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`.trim() || 'U';
  }
  if (name) {
    return (
      name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'U'
    );
  }
  // Extract initials from email prefix (e.g., "dp" from "dp@domelec.co.uk")
  if (email) {
    const emailPrefix = email.split('@')[0];
    if (emailPrefix) {
      return emailPrefix.slice(0, 2).toUpperCase();
    }
  }
  return 'U';
}

/**
 * Format current timestamp for notes attribution
 * Returns format like "15 Jan 2:30pm"
 */
export function formatNoteTimestamp(): string {
  const now = new Date();
  const day = now.getDate();
  const month = now.toLocaleDateString('en-GB', { month: 'short' });
  const hours = now.getHours();
  const minutes = now.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${day} ${month} ${hour12}:${minutes}${ampm}`;
}

/**
 * Format notes with styled attribution markers for display
 * Parses [XX - timestamp] patterns and renders as styled badges
 */
export function formatNotesWithAttribution(notes: string): ReactNode[] {
  if (!notes) return [];

  // Split by attribution pattern [XX - timestamp]
  const attributionPattern = /\[([A-Z]{1,3})\s*-\s*([^\]]+)\]/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = attributionPattern.exec(notes)) !== null) {
    // Add text before the attribution
    if (match.index > lastIndex) {
      const textBefore = notes.substring(lastIndex, match.index).trim();
      if (textBefore) {
        parts.push(createElement('span', { key: `text-${keyIndex++}` }, textBefore + ' '));
      }
    }

    // Add styled attribution badge
    const initials = match[1];
    const timestamp = match[2];
    parts.push(
      createElement(
        'span',
        {
          key: `attr-${keyIndex++}`,
          className:
            'inline-flex items-center gap-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded text-[10px] font-medium mr-1',
        },
        createElement('span', { className: 'font-bold' }, initials),
        createElement('span', { className: 'text-slate-500 dark:text-slate-400' }, '•'),
        createElement('span', null, timestamp)
      )
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last attribution
  if (lastIndex < notes.length) {
    const remaining = notes.substring(lastIndex).trim();
    if (remaining) {
      parts.push(createElement('span', { key: `text-${keyIndex++}` }, remaining));
    }
  }

  // If no attributions found, return the original text
  if (parts.length === 0) {
    return [createElement('span', { key: 'plain' }, notes)];
  }

  return parts;
}
